// Copyright (c) Ensho Health, 2021 to present

import api from '../api';
import actions from './index.js';
import { setNotification, NOTIFICATION_TYPES } from './notification.action.js';
import { REFERENCE_LABELS } from '../../constants.js';
import { formatFilters } from './actions.utils.js';

const PREFIX = 'lab-result';
export const ACTION_TYPES = {
  SET_LIST: `${PREFIX}-SET_LIST`,
  SET_DETAIL: `${PREFIX}-SET_DETAIL`,
  RESET_OFFSET: `${PREFIX}-RESET_OFFSET`,
  SET_FILTERS: `${PREFIX}-SET_FILTERS`,
  RESET_FILTERS: `${PREFIX}-RESET_FILTERS`,
  SET_ORDERING_PHY: `${PREFIX}-SET_ORDERING_PHY`,
  LOADING: `${PREFIX}-LOADING`,
  ERROR: `${PREFIX}-ERROR`
};

export function fetch() {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });

      const state = getState();
      const res = await api.labResult.fetchResults(
        formatFilters(state.labResult.filters),
        state.labResult.offset,
        state.labResult.limit,
        null
      );
      dispatch({
        type: ACTION_TYPES.SET_LIST,
        payload: res
      });
      const patientIds = res.map((lab) => lab.patientId);
      dispatch({
        type: actions.pii.ACTION_TYPES.ADD_PATIENTS,
        payload: patientIds
      });
      await dispatch(actions.pii.fetchPii());
    } catch (error) {
      console.error(error.message);
      dispatch(
        setNotification({
          type: NOTIFICATION_TYPES.ERROR,
          serverMessage: error.message,
          display: 'Error while fetching the lab results',
          info: { location: 'lab-result.action.fetch' }
        })
      );
      dispatch({ type: ACTION_TYPES.ERROR, payload: error.message });
    }
  };
}

function buildContext(rrm) {
  let startRadius = rrm.start_radius;
  let endRadius = rrm.end_radius;
  // older evidences may not have a start and end radius:
  if (startRadius === null || startRadius === undefined) {
    const contextRadius = rrm.context_radius;
    startRadius = contextRadius;
    endRadius = contextRadius;
  }
  const fullContext = rrm.context;
  const offset = rrm.offset;
  const length = rrm.length;

  const start = Math.max(0, offset - startRadius);
  const end = Math.min(fullContext.length, offset + length + endRadius);

  const contextPre = fullContext.substring(start, offset);
  const contextIn = fullContext.substring(offset, offset + length);
  const contextPost = fullContext.substring(offset + length, end);

  const rrmId = rrm.resultreviewmeta_id;
  return [
    <span key={rrmId + 'context-pre'}>{contextPre}</span>,
    <span key={rrmId + 'context-in'} style={{ backgroundColor: '#c8e7f9' }}>
      {contextIn}
    </span>,
    <span key={rrmId + 'context-post'}>{contextPost}</span>
  ];
}

function extractEvidence(evidence) {
  const context = buildContext(evidence.result_review_metadata);
  let res = {
    tcp_id: evidence.tcp_id,
    tcpe_id: evidence.tcpe_id,
    reference: evidence.query_result.reference,
    context: context,
    rrmId: evidence.result_review_metadata.resultreviewmeta_id,
    doc_title: evidence.result_review_metadata.doc_title,
    doc_date: evidence.result_review_metadata.doc_date
  };
  res.referenceLabel =
    REFERENCE_LABELS[res.reference] || res.reference + ' (no label)';
  return res;
}
/*
  The goal is to generate a list of evidences for each criteria in the result.
  A criteria is identified by its reference.
  Ultimately, for the corroborating symptoms for example, we want a final
  object shaped like:
  {
    otherManifestations: {
      reference1:[
        evidence1:{rrmId, reference, ...},
        evidence2,
        ...
      ],
      reference2:[
        evidence3,
        ...
      ]
    },
    cardiacManifestations: {
      ...
    }
  };
*/
function formatCa4fEvidences(otherEvidences) {
  const res = {
    nbOtherManifestations: 0,
    nbCardiacManifestations: 0,
    nbConflicting: 0,
    nbInvestigations: 0,
    otherManifestations: {},
    cardiacManifestations: {},
    conflicting: {},
    investigations: {}
  };

  // dispatching the actual evidences, creating the right format
  otherEvidences.corroborating.other_manifestations.forEach((evidence) => {
    const e = extractEvidence(evidence);
    if (!res.otherManifestations[e.referenceLabel]) {
      res.otherManifestations[e.referenceLabel] = [];
    }
    res.otherManifestations[e.referenceLabel].push(e);
  });
  otherEvidences.corroborating.cardiac_manifestations.forEach((evidence) => {
    const e = extractEvidence(evidence);
    if (!res.cardiacManifestations[e.referenceLabel]) {
      res.cardiacManifestations[e.referenceLabel] = [];
    }
    res.cardiacManifestations[e.referenceLabel].push(e);
  });
  otherEvidences.conflicting.conflicting.forEach((evidence) => {
    const e = extractEvidence(evidence);
    if (!res.conflicting[e.referenceLabel]) {
      res.conflicting[e.referenceLabel] = [];
    }
    res.conflicting[e.referenceLabel].push(e);
  });
  otherEvidences.investigations.investigations.forEach((evidence) => {
    const e = extractEvidence(evidence);
    if (!res.investigations[e.referenceLabel]) {
      res.investigations[e.referenceLabel] = [];
    }
    res.investigations[e.referenceLabel].push(e);
  });
  // nb criteria for each category
  res.nbOtherManifestations = Object.keys(res.otherManifestations).length;
  res.nbCardiacManifestations = Object.keys(res.cardiacManifestations).length;
  res.nbConflicting = Object.keys(res.conflicting).length;
  res.nbInvestigations = Object.keys(res.investigations).length;

  return res;
}

/**
 * Given a result id, fetch the detail of the lab result
 */
export function fetchDetail(resultId) {
  return async (dispatch) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      const labDetail = await api.labResult.fetchDetails(resultId);
      labDetail.formattedEvidence = labDetail.otherEvidence;
      // formatting other evidences so that it's easily handled
      // in the interface
      if (labDetail.ca4fResultId) {
        labDetail.formattedEvidence = formatCa4fEvidences(
          labDetail.otherEvidence
        );
      }

      dispatch({
        type: ACTION_TYPES.SET_DETAIL,
        payload: labDetail
      });
    } catch (error) {
      console.error(error.message);
      dispatch(
        setNotification({
          type: NOTIFICATION_TYPES.ERROR,
          serverMessage: error.message,
          info: {
            location: 'lab-result.action.fetchDetail',
            resultId: resultId
          },
          display: `Error while fetching the detail for lab results ${resultId}. Please contact support if this problem persists.`
        })
      );
      dispatch({ type: ACTION_TYPES.ERROR, payload: error.message });
    }
  };
}

/**
 * fetch a list of all ordering physicians
 */
export function fetchOrderingPhysician() {
  return async (dispatch) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      const ordering_phy_list = await api.labResult.fetchOrderingPhy();
      dispatch({
        type: ACTION_TYPES.SET_ORDERING_PHY,
        payload: ordering_phy_list
      });
    } catch (error) {
      console.error(error.message);
      dispatch(
        setNotification({
          type: NOTIFICATION_TYPES.ERROR,
          serverMessage: error.message,
          info: {
            location: 'lab-result.action.fetchOrderingPhysician'
          },
          display: `Error while fetching the ordering physicians. Please contact support if this problem persists.`
        })
      );
      dispatch({ type: ACTION_TYPES.ERROR, payload: error.message });
    }
  };
}

/**
 * Send a support request to the server to alert an Ensho admin
 */
export async function submitSupport(info) {
  try {
    const supportResponse = await api.labResult.submitSupport(info);
    if (supportResponse.error) {
      throw new Error(supportResponse.error);
    } else {
      return true;
    }
  } catch (error) {
    console.error(error.message);
    const serverNotification = {
      message: error.message,
      info: { location: 'lab-result.action.submitSupport', support: info },
      type: NOTIFICATION_TYPES.ERROR
    };
    await api.notification.sendNotification(serverNotification);
    throw error;
  }
}

// download a lab result report
export async function dlReport(trialId, ca4fResultId, hcn, patientName, dob) {
  try {
    return api.labResult.dlReport(trialId, ca4fResultId, hcn, patientName, dob);
  } catch (error) {
    console.error(error.message);
  }
}

// reset the resuilt list offset. For example when changing the filters.
export function resetOffset() {
  return { type: ACTION_TYPES.RESET_OFFSET };
}

// set the lab results filters that must be saved in the state manager for
// persistence across several pages
export function setFilters(filters) {
  return { type: ACTION_TYPES.SET_FILTERS, payload: filters };
}

// set the lab results filters that must be saved in the state manager for
// persistence across several pages
export function resetFilters() {
  return { type: ACTION_TYPES.RESET_FILTERS };
}
